/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 */

/**
 *
 * @constructor
 */

import * as Debug from "App/debug";
import DynamicAssetsManager from "App/storage/dynamic_assets_manager";

export class ImageManagerItem {
    private _loaded: boolean = false;
    private _callbacks: any[];
    private _image: HTMLImageElement = new Image();
    private _xhr: XMLHttpRequest;

    constructor(url: string, cb: (image: HTMLImageElement) => void) {
        this._callbacks = [cb];

        let onload = (): void => {
            // @ts-ignore
            let urlCreator = window.URL || window.webkitURL;

            if (this._xhr.status === 200) {
                this._image.onload = (): void => {
                    this._loaded = true;
                    for (let callbacks of this._callbacks) {
                        callbacks(this._image);
                    }
                    this._callbacks = [];
                    this._xhr = null;
                    urlCreator.revokeObjectURL(this._image.src);
                };

                let arrayBufferView = new Uint8Array(this._xhr.response);
                let blob = new Blob([arrayBufferView.buffer], {type: "image/png"});
                this._image.src = urlCreator.createObjectURL(blob);
            }
        };

        let onerror = (): void => {
            Debug.logError("Resource download error '" + this._image.src + "'");
        };

        // by using XMLHttpRequest we avoid cross resource problem
        // TB: this is still a little hacky code, at least not correct
        // There should be a proper way in engine to download a texture from server
        if (url.indexOf("@asset") !== -1) {
            DynamicAssetsManager.loadAsset(url, (asset: any) => {
                //@ts-ignore
                //console.log("url:" + url);
                //console.log("Asset:" + asset);
                this._xhr = new XMLHttpRequest();
                this._xhr.addEventListener('load', onload);
                this._xhr.addEventListener('error', onerror);

                let content =  asset.default ? asset.default : asset;
                if (content.indexOf("data:") !== -1) { // in url data
                    this._xhr.open('GET', content, true);
                }
                else {
                    this._xhr.open('GET', content.replace(/\\/g, "/"), true);
                }
                this._xhr.responseType = 'arraybuffer';
                this._xhr.send(null);
            });
        }
        else if (url.indexOf(";base64,") === -1) {
            this._xhr = new XMLHttpRequest();
            this._xhr.addEventListener('load', onload);
            this._xhr.addEventListener('error', onerror);
            this._xhr.open('GET', url, true);
            this._xhr.responseType = 'arraybuffer';

            this._xhr.send(null);
        }
        else {
            // we should remove this class completely asap and use the engine data storage and textures handling
            // the compass should be in internal model which was designed for all rendered objects which are in environment ...
            this._image.onload = (): void => {
                this._loaded = true;
                // logInfo("Image loaded: " + self._src); // do not write the name to console as it can contain the image in base64 data
                for (let callback of this._callbacks) {
                    callback(url, this._image);
                }
                this._callbacks = [];
                this._xhr = null;
            };

            this._image.src = url;
        }
    }

    public getImage(): HTMLImageElement {
        return this._image;
    }

    public isLoaded(): boolean {
        return this._loaded;
    }

    public addCallback(cb: (image: HTMLImageElement) => void): void {
        // Call from cache
        if (this._loaded) {
            cb(this._image);
        }
        // Or add to queue
        else {
            this._callbacks.push(cb);
        }
    }
}
/**
 * Simple image manager only for environment
 *
 * @constructor
 */
export class ImageManager {
    /** @type [ImageManagerItem] */
    private _images: {} = {};

    constructor() { /**/ }

    /**
     * @param {function(string,Image)} cb
     */
    public getImage(url: string, cb: (image: HTMLImageElement) => void) {
        // Check cache
        let image = this._images[url];
        if (image) {
            image.addCallback(cb);
            return;
        }

        // Load new
        this._images[url] = new ImageManagerItem(url, cb);
    }
}
